import React, { useEffect, useRef, useState } from "react";
import { Link, NavLink, useNavigate, useLocation } from "react-router-dom";
import { ShoppingBag, Search, Menu, X, ChevronDown } from "lucide-react";
import { useCart } from "../context/CartContext";
import { api } from "../lib/api";

// Category list shown in the Shop mega-menu.
// Order is editorial-intentional, not alphabetical.
// `fallback` is used when no product image is available yet for that category —
// keeps the dropdown looking polished even on a thin catalog.
const SHOP_CATEGORIES = [
  { label: "Totes", category: "Tote", fallback: "https://images.unsplash.com/photo-1590739225497-56a04a1cd54e?w=600&h=750&fit=crop&q=80" },
  { label: "Crossbody", category: "Crossbody", fallback: "https://images.unsplash.com/photo-1559563458-527698bf5295?w=600&h=750&fit=crop&q=80" },
  { label: "Shoulder", category: "Shoulder", fallback: "https://images.unsplash.com/photo-1566150905458-1bf1fc113f0d?w=600&h=750&fit=crop&q=80" },
  { label: "Top Handle", category: "Top Handle", fallback: "https://images.unsplash.com/photo-1584917865442-de89df76afd3?w=600&h=750&fit=crop&q=80" },
  { label: "Bucket", category: "Bucket", fallback: "https://images.unsplash.com/photo-1548036328-c9fa89d128fa?w=600&h=750&fit=crop&q=80" },
  { label: "Backpacks", category: "Backpack", fallback: "https://images.unsplash.com/photo-1577733966973-d680bffd2e80?w=600&h=750&fit=crop&q=80" },
  { label: "Clutch", category: "Clutch", fallback: "https://images.unsplash.com/photo-1614179689702-355944cd0918?w=600&h=750&fit=crop&q=80" },
  { label: "Mini", category: "Mini", fallback: "https://images.unsplash.com/photo-1591561954557-26941169b49e?w=600&h=750&fit=crop&q=80" },
];

export default function Header() {
  const { count, setDrawerOpen } = useCart();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [mobileShopOpen, setMobileShopOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [shopOpen, setShopOpen] = useState(false);
  const [q, setQ] = useState("");
  const [productsByCategory, setProductsByCategory] = useState({});
  const [newArrivals, setNewArrivals] = useState([]);
  const navigate = useNavigate();
  const location = useLocation();
  const closeTimer = useRef(null);

  // Pre-fetch one representative product image per category for the mega-menu
  // and the latest 2 pieces (for the "Just Added" rail). Single GET reused.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await api.get("/products", { params: { limit: 200, sort: "newest" } });
        if (cancelled) return;
        const list = r.data || [];
        const map = {};
        for (const p of list) {
          if (p.category && !map[p.category] && p.images && p.images[0]) {
            map[p.category] = { image: p.images[0], name: p.name };
          }
        }
        setProductsByCategory(map);
        setNewArrivals(list.slice(0, 2));
      } catch (_) {
        if (!cancelled) {
          setProductsByCategory({});
          setNewArrivals([]);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // Close all flyouts on route change
  useEffect(() => {
    setShopOpen(false);
    setMobileOpen(false);
    setMobileShopOpen(false);
    setSearchOpen(false);
  }, [location.pathname, location.search]);

  const submitSearch = (e) => {
    e.preventDefault();
    if (q.trim()) {
      navigate(`/shop?q=${encodeURIComponent(q.trim())}`);
      setSearchOpen(false);
      setQ("");
    }
  };

  // Hover-with-delay: opens immediately, closes after 120ms grace so the
  // pointer can travel from trigger to flyout without flicker.
  const openShop = () => {
    if (closeTimer.current) {
      clearTimeout(closeTimer.current);
      closeTimer.current = null;
    }
    setShopOpen(true);
  };
  const scheduleCloseShop = () => {
    if (closeTimer.current) clearTimeout(closeTimer.current);
    closeTimer.current = setTimeout(() => setShopOpen(false), 120);
  };

  return (
    <header className="glass-nav fixed top-0 left-0 right-0 z-50" data-testid="site-header">
      <div className="max-w-[1600px] mx-auto px-6 md:px-12">
        <div className="flex items-center justify-between h-20">
          <button
            data-testid="mobile-menu-toggle"
            onClick={() => setMobileOpen(!mobileOpen)}
            className="md:hidden p-2 -ml-2"
            aria-label="Menu"
          >
            {mobileOpen ? <X size={20} strokeWidth={1.5} /> : <Menu size={20} strokeWidth={1.5} />}
          </button>

          <Link
            to="/"
            className="font-display text-3xl md:text-4xl tracking-tight font-bold leading-none select-none"
            data-testid="brand-logo"
          >
            Elegant<span className="italic font-medium ml-1">Affairs</span>
          </Link>

          <nav className="hidden md:flex items-center gap-10" aria-label="Primary">
            {/* Shop trigger — also a clickable link to /shop */}
            <div
              className="relative"
              onMouseEnter={openShop}
              onMouseLeave={scheduleCloseShop}
              data-testid="shop-dropdown-wrapper"
            >
              <button
                type="button"
                onClick={() => (shopOpen ? setShopOpen(false) : setShopOpen(true))}
                aria-expanded={shopOpen}
                aria-haspopup="true"
                data-testid="nav-shop"
                className={`text-[0.78rem] uppercase tracking-[0.22em] inline-flex items-center gap-1.5 transition-colors ${
                  location.pathname === "/shop" || shopOpen
                    ? "text-[var(--mn-ink)]"
                    : "text-[var(--mn-ink-2)] hover:text-[var(--mn-ink)]"
                }`}
              >
                Shop
                <ChevronDown
                  size={12}
                  strokeWidth={1.5}
                  className={`transition-transform duration-200 ${shopOpen ? "rotate-180" : ""}`}
                />
              </button>
            </div>

            <NavLink
              to="/about"
              data-testid="nav-about"
              className={({ isActive }) =>
                `text-[0.78rem] uppercase tracking-[0.22em] transition-colors ${
                  isActive ? "text-[var(--mn-ink)]" : "text-[var(--mn-ink-2)] hover:text-[var(--mn-ink)]"
                }`
              }
            >
              About
            </NavLink>
          </nav>

          <div className="flex items-center gap-2 md:gap-4">
            <button
              data-testid="search-toggle"
              onClick={() => setSearchOpen(!searchOpen)}
              className="p-2 hover:opacity-70 transition-opacity"
              aria-label="Search"
            >
              <Search size={18} strokeWidth={1.5} />
            </button>
            <Link
              to="/admin"
              className="hidden md:inline text-[0.7rem] uppercase tracking-[0.22em] text-[var(--mn-ink-2)] hover:text-[var(--mn-ink)]"
              data-testid="nav-admin"
            >
              Manage
            </Link>
            <button
              data-testid="open-cart-button"
              onClick={() => setDrawerOpen(true)}
              className="relative p-2 hover:opacity-70 transition-opacity"
              aria-label="Cart"
            >
              <ShoppingBag size={18} strokeWidth={1.5} />
              {count > 0 && (
                <span
                  data-testid="cart-count-badge"
                  className="absolute -top-0.5 -right-0.5 bg-[var(--mn-accent)] text-white text-[10px] font-mono-luxe min-w-[18px] h-[18px] px-1 flex items-center justify-center"
                >
                  {count}
                </span>
              )}
            </button>
          </div>
        </div>

        {searchOpen && (
          <form onSubmit={submitSearch} className="pb-5 fade-up" data-testid="search-form">
            <input
              autoFocus
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search the collection…"
              className="w-full bg-transparent border-b border-[var(--mn-border-strong)] py-3 text-lg font-display italic placeholder:text-[var(--mn-muted)] focus:outline-none"
              data-testid="search-input"
            />
          </form>
        )}

        {mobileOpen && (
          <MobileNav
            shopOpen={mobileShopOpen}
            setShopOpen={setMobileShopOpen}
            categoryImages={productsByCategory}
            onClose={() => setMobileOpen(false)}
          />
        )}
      </div>

      {/* Desktop mega-menu (full-width flyout) */}
      <ShopMegaMenu
        open={shopOpen}
        onMouseEnter={openShop}
        onMouseLeave={scheduleCloseShop}
        categoryImages={productsByCategory}
        newArrivals={newArrivals}
      />
    </header>
  );
}

function ShopMegaMenu({ open, onMouseEnter, onMouseLeave, categoryImages, newArrivals = [] }) {
  return (
    <div
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      data-testid="shop-mega-menu"
      aria-hidden={!open}
      className={`hidden md:block absolute left-0 right-0 top-20 bg-[var(--mn-bg)] border-t border-[var(--mn-border)] shadow-[0_24px_48px_-24px_rgba(18,18,18,0.18)] origin-top transition-all duration-200 ${
        open ? "opacity-100 translate-y-0 pointer-events-auto" : "opacity-0 -translate-y-2 pointer-events-none"
      }`}
    >
      <div className="max-w-[1600px] mx-auto px-6 md:px-12 py-12">
        <div className="grid grid-cols-12 gap-10">
          {/* Editorial intro */}
          <div className="col-span-3">
            <p className="eyebrow">The Catalogue</p>
            <h3 className="font-display text-4xl mt-3 leading-[0.95] tracking-tighter">
              Shop by <em className="font-light">silhouette</em>.
            </h3>
            <p className="text-sm text-[var(--mn-ink-2)] mt-5 leading-relaxed">
              Each category is hand-curated. Every piece authenticated before it ships.
            </p>
            <Link
              to="/shop"
              className="inline-flex items-center gap-1.5 mt-6 text-[0.7rem] uppercase tracking-[0.22em] hover:text-[var(--mn-accent)]"
              data-testid="mega-view-all"
            >
              View all pieces →
            </Link>
          </div>

          {/* Just Added — 2 latest products */}
          <div className="col-span-3 border-l border-[var(--mn-border)] pl-10" data-testid="mega-new-arrivals">
            <div className="flex items-baseline justify-between mb-5">
              <p className="eyebrow text-[var(--mn-accent)]">Just Added</p>
              <Link
                to="/shop?sort=newest"
                className="text-[10px] uppercase tracking-[0.22em] text-[var(--mn-ink-2)] hover:text-[var(--mn-accent)]"
                data-testid="mega-new-arrivals-all"
              >
                All new →
              </Link>
            </div>
            {newArrivals.length === 0 ? (
              <p className="text-xs text-[var(--mn-muted)] italic">New pieces will appear here as they're added.</p>
            ) : (
              <ul className="space-y-5">
                {newArrivals.map((p) => (
                  <li key={p.id}>
                    <Link
                      to={`/product/${p.id}`}
                      className="group flex items-center gap-4"
                      data-testid={`mega-new-${p.id}`}
                    >
                      <div className="w-20 h-24 bg-neutral-100 overflow-hidden shrink-0">
                        {p.images && p.images[0] && (
                          <img
                            src={p.images[0]}
                            alt=""
                            loading="lazy"
                            className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                          />
                        )}
                      </div>
                      <div className="min-w-0">
                        <p className="eyebrow text-[var(--mn-muted)] truncate">{p.brand}</p>
                        <p className="font-display text-lg leading-tight mt-1 truncate group-hover:text-[var(--mn-accent)] transition-colors">
                          {p.name}
                        </p>
                        <p className="font-mono-luxe text-xs text-[var(--mn-ink)] mt-1">
                          ${Number(p.price).toLocaleString("en-US", { maximumFractionDigits: 0 })}
                        </p>
                      </div>
                    </Link>
                  </li>
                ))}
              </ul>
            )}
          </div>

          {/* Categories grid */}
          <ul className="col-span-6 grid grid-cols-4 gap-x-6 gap-y-8">
            {SHOP_CATEGORIES.map((c) => {
              const img = categoryImages[c.category]?.image || c.fallback;
              return (
                <li key={c.category}>
                  <Link
                    to={`/shop?category=${encodeURIComponent(c.category)}`}
                    className="group block"
                    data-testid={`mega-cat-${c.category.toLowerCase().replace(/\s+/g, "-")}`}
                  >
                    <div className="aspect-[4/5] bg-neutral-100 overflow-hidden">
                      {img ? (
                        <img
                          src={img}
                          alt=""
                          loading="lazy"
                          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center text-[var(--mn-muted)]">
                          <span className="font-display text-3xl tracking-tighter italic">EA</span>
                        </div>
                      )}
                    </div>
                    <p className="font-display text-lg mt-3 leading-tight group-hover:text-[var(--mn-accent)] transition-colors">
                      {c.label}
                    </p>
                    <p className="text-[10px] uppercase tracking-[0.22em] text-[var(--mn-muted)] mt-1">
                      Shop the edit
                    </p>
                  </Link>
                </li>
              );
            })}
          </ul>
        </div>
      </div>
    </div>
  );
}

function MobileNav({ shopOpen, setShopOpen, categoryImages, onClose }) {
  return (
    <div
      className="md:hidden border-t border-[var(--mn-border)] py-6"
      data-testid="mobile-nav"
    >
      <ul className="flex flex-col">
        <li>
          <button
            type="button"
            onClick={() => setShopOpen(!shopOpen)}
            className="w-full flex items-center justify-between py-3 text-sm uppercase tracking-[0.22em] text-[var(--mn-ink)]"
            aria-expanded={shopOpen}
            data-testid="mnav-shop"
          >
            <span>Shop</span>
            <ChevronDown
              size={14}
              strokeWidth={1.5}
              className={`transition-transform duration-200 ${shopOpen ? "rotate-180" : ""}`}
            />
          </button>
          {shopOpen && (
            <ul className="grid grid-cols-2 gap-3 mt-2 mb-4 pl-1" data-testid="mnav-shop-categories">
              <li>
                <Link
                  to="/shop"
                  onClick={onClose}
                  className="block py-3 px-3 border border-[var(--mn-border-strong)] text-xs uppercase tracking-[0.22em] hover:bg-[var(--mn-ink)] hover:text-[var(--mn-bg)]"
                  data-testid="mnav-shop-all"
                >
                  All pieces
                </Link>
              </li>
              {SHOP_CATEGORIES.map((c) => {
                const img = categoryImages[c.category]?.image || c.fallback;
                return (
                  <li key={c.category}>
                    <Link
                      to={`/shop?category=${encodeURIComponent(c.category)}`}
                      onClick={onClose}
                      className="flex items-center gap-3 p-2 border border-[var(--mn-border)] hover:border-[var(--mn-ink)]"
                      data-testid={`mnav-cat-${c.category.toLowerCase().replace(/\s+/g, "-")}`}
                    >
                      <div className="w-12 h-14 bg-neutral-100 overflow-hidden shrink-0">
                        {img && <img src={img} alt="" loading="lazy" className="w-full h-full object-cover" />}
                      </div>
                      <span className="font-display text-base leading-tight">{c.label}</span>
                    </Link>
                  </li>
                );
              })}
            </ul>
          )}
        </li>
        <li>
          <Link
            to="/about"
            onClick={onClose}
            className="block py-3 text-sm uppercase tracking-[0.22em] text-[var(--mn-ink)]"
            data-testid="mnav-about"
          >
            About
          </Link>
        </li>
        <li>
          <Link
            to="/admin"
            onClick={onClose}
            className="block py-3 text-sm uppercase tracking-[0.22em] text-[var(--mn-ink-2)]"
            data-testid="mnav-admin"
          >
            Manage
          </Link>
        </li>
      </ul>
    </div>
  );
}
